import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Pressable, Image, Modal } from 'react-native';
import { Feather } from '@expo/vector-icons';
import Toast from 'react-native-root-toast';

import { Text } from '../../../components';
import { SecondaryButton } from '../../../design/components';
import { useTheme } from '../../../theme';
import { TextInputEditInPlace } from '../../../components/input';
import { ImageViewer } from '../../../components/image';
import withMediaPicker from '../../../hocs/withMediaPicker';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

const SetupPhotoSectionBase = ({
	title,
	photos = [],
	captureMedia,
	capturedMediaLink,
	onAddPhoto,
	onUpdateComment,
	onRemovePhoto,
	addButtonLabel = 'Add photo',
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [viewerState, setViewerState] = useState({ open: false, index: 0 });
	const [processedLink, setProcessedLink] = useState(null);

	useEffect(() => {
		if (!capturedMediaLink) {
			return;
		}

		if (capturedMediaLink === processedLink) {
			return;
		}

		setProcessedLink(capturedMediaLink);
		(async () => {
			try {
				await onAddPhoto(capturedMediaLink);
			}
			catch (error) {
				Toast.show('Unable to upload photo');
			}
		})();
	}, [capturedMediaLink, onAddPhoto, processedLink]);

	const openViewer = useCallback((index) => {
		setViewerState({ open: true, index });
	}, []);

	const closeViewer = useCallback(() => {
		setViewerState({ open: false, index: 0 });
	}, []);

	const viewerImages = useMemo(() => {
		return photos.map(photo => ({ url: photo.link }));
	}, [photos]);

	return (
		<View style={styles.section}>
			<Text style={styles.sectionTitle}>{title}</Text>
			{onAddPhoto && (
				<View style={styles.actionsRow}>
					<SecondaryButton
						label={addButtonLabel}
						icon="camera"
						onPress={captureMedia}
					/>
				</View>
			)}
			{
				photos.length === 0 && (
					<Text style={styles.emptyText}>No photos yet</Text>
				)
			}
			{
				photos.length > 0 && (
					<View style={styles.grid}>
						{
							photos.map((photo, index) => (
								<View key={photo.id} style={styles.card}>
									<Pressable
										onPress={() => openViewer(index)}
										accessibilityRole="imagebutton"
										accessibilityLabel="Open photo"
									>
										<Image
											source={{ uri: photo.link }}
											style={styles.image}
										/>
									</Pressable>
									{onRemovePhoto && (
										<Pressable
											onPress={() => onRemovePhoto(photo)}
											style={({ pressed }) => [styles.removeButton, pressed && styles.pressed]}
											accessibilityRole="button"
											accessibilityLabel="Remove photo"
										>
											<View style={styles.removeCircle}>
												<Feather name="x" size={16} color={colors.icon} />
											</View>
										</Pressable>
									)}
									{onUpdateComment ? (
										<TextInputEditInPlace
											value={photo.comment}
											field="comment"
											placeholder="Add a note"
											onConfirm={(payload) => onUpdateComment(photo, payload.comment)}
											multiline={true}
											numberOfLines={3}
											style={styles.commentInput}
										/>
									) : (
										photo.comment ? <Text style={styles.commentText}>{photo.comment}</Text> : null
									)}
								</View>
							))
						}
					</View>
				)
			}

			{
				viewerState.open && (
					<Modal
						visible={true}
						transparent={true}
						onRequestClose={closeViewer}
					>
						<ImageViewer
							close={closeViewer}
							index={viewerState.index}
							imageUrls={viewerImages}
						/>
					</Modal>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	section: {
		marginBottom: space.lg,
	},
	sectionTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
		marginBottom: space.sm,
	},
	actionsRow: {
		alignItems: 'flex-start',
		marginBottom: space.md,
	},
	emptyText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	grid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginHorizontal: -6,
	},
	card: {
		width: '48%',
		marginHorizontal: '1%',
		marginBottom: space.md,
		backgroundColor: colors.raised,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		padding: space.sm,
		borderRadius: radius.card,
	},
	removeButton: {
		position: 'absolute',
		top: space.xs,
		right: space.xs,
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	removeCircle: {
		width: 28,
		height: 28,
		borderRadius: radius.pill,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.surface,
	},
	pressed: {
		opacity: 0.7,
	},
	image: {
		width: '100%',
		height: 120,
		borderRadius: radius.chip,
		marginBottom: space.sm,
	},
	commentInput: {
		minHeight: 60,
		textAlignVertical: 'top',
	},
	commentText: {
		...type.secondary,
		color: colors.text,
	},
});

const WrappedSetupPhotoSection = withMediaPicker(SetupPhotoSectionBase);

const SetupPhotoSection = (props) => {
	return (
		<WrappedSetupPhotoSection
			allowMultipleSelection={true}
			allowMultipleCapture={true}
			{...props}
		/>
	);
};

export default SetupPhotoSection;
